import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { FileText, Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AnswerKeyTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/marking/templates`, { withCredentials: true });
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading templates...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <button
              onClick={() => navigate('/assessment')}
              className="text-purple-600 hover:text-purple-700 font-medium mb-4 inline-flex items-center gap-2"
            >
              ← Back to Assessment Hub
            </button>
            
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Answer Key Templates</h1>
            <p className="text-base text-slate-600">Save and reuse answer keys for common tests</p>
          </div>

          {templates.length === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No templates saved yet</h3>
              <p className="text-slate-600 mb-6">
                Create a marking project with an answer key, then save it as a template for future use
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {templates.map((template) => (
                <div key={template.template_id} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{template.template_name}</h3>
                  <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                    <span>📚 {template.subject}</span>
                    <span>🎓 Grade {template.grade_level}</span>
                    <span>🌍 {template.curriculum}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                      {template.answer_key.length} question{template.answer_key.length !== 1 ? 's' : ''}
                    </p>
                    <button className="text-purple-600 hover:text-purple-700 font-semibold text-sm">
                      Use Template →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default AnswerKeyTemplates;
